import type { QueryClient } from "@tanstack/react-query";
import { useQueryClient } from "@tanstack/react-query";
import isEqual from "lodash/isEqual";
import { useEffect, useMemo, useState } from "react";
import { createTimeTrackingEventsMutationKey } from "~components/TimeManagement/api/createTimeTrackingEventsMutationKey";

export interface UseTimeTrackingSyncStatusResult {
  isSynchronized: boolean;
  isMutating: boolean;
}

const getTimeTrackingEventMutations = (queryClient: QueryClient) =>
  queryClient
    .getMutationCache()
    .getAll()
    .filter(({ options: { mutationKey } }) =>
      // createTimeTrackingEventsMutationKey is an array. After reload from indexeddb the object identity changes
      isEqual(mutationKey, createTimeTrackingEventsMutationKey),
    );

const getMutationCounts = (
  queryClient: QueryClient,
): [pausedCount: number, loadingCount: number] => {
  const mutations = getTimeTrackingEventMutations(queryClient);

  return [
    mutations.filter(({ state: { isPaused } }) => isPaused).length,
    mutations.filter(({ state: { status } }) => status === "pending").length,
  ];
};

export function useTimeTrackingSyncStatus() {
  const queryClient = useQueryClient();

  const [initialPausedCount, initialLoadingCount] = useMemo(
    () => getMutationCounts(queryClient),
    [queryClient],
  );

  const [paused, setPaused] = useState(initialPausedCount);
  const [loading, setLoading] = useState(initialLoadingCount);

  useEffect(
    () =>
      queryClient.getMutationCache().subscribe(({ type }) => {
        if (!["added", "updated"].includes(type)) {
          return;
        }

        const [nextPausedCount, nextLoadingCount] =
          getMutationCounts(queryClient);

        setPaused(nextPausedCount);
        setLoading(nextLoadingCount);
      }),
    [queryClient],
  );

  return {
    isSynchronized: paused === 0 && loading === 0,
    isMutating: paused !== loading,
  };
}
